import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, MapPin, Bookmark, Eye, Share2, Clock, Package, ToggleLeft, ToggleRight, PhoneCall, CheckCircle2, Trash2, PackageX, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { trackProduceView, trackContactClick } from '@/lib/analytics';
import StarRating from '@/components/StarRating';
import LikeFarmerButton from '@/components/LikeFarmerButton';
import NotificationBell from '@/components/NotificationBell';
import BottomNav from '@/components/BottomNav';
import ConfirmSheet from '@/components/ConfirmSheet';
import Avatar from '@/components/Avatar';

export default function ProduceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [callbackReq, setCallbackReq] = useState(null); // existing open request for this post
  const [requestingCallback, setRequestingCallback] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [farmerSummary, setFarmerSummary] = useState(null); // { avg_rating, review_count, is_followed }
  const [adminToggleOpen, setAdminToggleOpen] = useState(false);
  const [adminDeleteOpen, setAdminDeleteOpen] = useState(false);
  const [adminDeleting, setAdminDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isOwner = user && post && post.farmer_id === user._id;

  const loadFarmerSummary = useCallback(async (farmerId) => {
    try {
      const { data } = await api.get(`/farmer/${farmerId}/profile`);
      setFarmerSummary({
        avg_rating: data.avg_rating || 0,
        review_count: data.review_count || 0,
        is_followed: !!data.is_followed,
      });
    } catch {}
  }, [api]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data } = await api.get(`/produce/${id}`);
        setPost(data);
        trackProduceView(id, data.title);
        if (data.farmer_id) loadFarmerSummary(data.farmer_id);
      } catch {
        toast.error('Produce not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    const checkSaved = async () => {
      try {
        const { data } = await api.get('/saved/ids');
        setIsSaved((data.post_ids || []).includes(id));
      } catch {}
    };
    const checkCallback = async () => {
      try {
        const { data } = await api.get('/callback-requests/mine', { params: { post_id: id } });
        setCallbackReq(data.request || null);
      } catch {}
    };
    fetchPost();
    checkSaved();
    checkCallback();
  }, [api, id, navigate, loadFarmerSummary]);

  const handleSave = async () => {
    try {
      const { data } = await api.post(`/saved/${id}`);
      setIsSaved(data.saved);
      toast.success(data.saved ? 'Saved!' : 'Removed from saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleToggleStatus = async () => {
    if (!post) return;
    setTogglingStatus(true);
    try {
      const next = !post.is_available;
      await api.put(`/produce/${id}`, { is_available: next });
      setPost(prev => ({ ...prev, is_available: next }));
      toast.success(next ? 'Marked as available' : 'Marked as sold out');
    } catch {
      toast.error('Failed to update status');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleAdminDelete = async () => {
    setAdminDeleting(true);
    try {
      await api.delete(`/produce/${id}`);
      toast.success('Post deleted');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setAdminDeleting(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied!');
    }
  };

  const handleRequestCallback = async () => {
    setRequestingCallback(true);
    try {
      const { data } = await api.post('/callback-requests', { post_id: id });
      setCallbackReq(data.request);
      trackContactClick(post.farmer_id, post.farmer_name);
      toast.success(
        data.already_requested
          ? 'You already have an open request for this listing'
          : 'Request received! Our team will call you back shortly.'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRequestingCallback(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000 / 60);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]">
        <div className="animate-pulse">
          <div className="w-full h-72 bg-[#EFF2F7]" />
          <div className="p-4 space-y-4">
            <div className="h-6 bg-[#EFF2F7] rounded w-3/4" />
            <div className="h-4 bg-[#EFF2F7] rounded w-1/2" />
            <div className="h-20 bg-[#EFF2F7] rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]" data-testid="produce-detail-page">
      {/* Header */}
      <div className="relative">
        {/* Image */}
        <div className="w-full h-72 md:h-80 bg-[#EFF2F7] relative">
          {post.images?.[0] ? (
            <img src={post.images[0]} alt={post.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-6xl">🌾</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>

        {/* Top actions */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-[#F6F8FB]/90 backdrop-blur-sm flex items-center justify-center border border-[#E4E8EF]"
          >
            <ArrowLeft className="w-5 h-5 text-[#0B1620]" />
          </button>
          <div className="flex gap-2">
            <NotificationBell />
            <button
              data-testid="share-btn"
              onClick={handleShare}
              className="w-10 h-10 rounded-full bg-[#F6F8FB]/90 backdrop-blur-sm flex items-center justify-center border border-[#E4E8EF]"
            >
              <Share2 className="w-4 h-4 text-[#0B1620]" />
            </button>
            <button
              data-testid="save-detail-btn"
              onClick={handleSave}
              className="w-10 h-10 rounded-full bg-[#F6F8FB]/90 backdrop-blur-sm flex items-center justify-center border border-[#E4E8EF]"
            >
              <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-[#0B4F3F] text-[#0B4F3F]' : 'text-[#0B1620]'}`} />
            </button>
          </div>
        </div>

        {/* Category & availability badge */}
        <div className="absolute bottom-4 left-4 flex gap-2">
          <span className="px-3 py-1 bg-[#F6F8FB]/90 backdrop-blur-sm text-[#0B4F3F] text-xs font-semibold rounded-lg">
            {post.category}
          </span>
          {post.is_available ? (
            <span className="px-3 py-1 bg-[#15803D]/90 text-[#F6F8FB] text-xs font-semibold rounded-lg">
              Available
            </span>
          ) : (
            <span className="px-3 py-1 bg-[#B91C1C]/90 text-[#F6F8FB] text-xs font-semibold rounded-lg">
              Sold Out
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24 space-y-5">
        {/* Extra media (additional photos + videos) — the hero above shows images[0] */}
        {((post.images && post.images.length > 1) || (post.videos && post.videos.length > 0)) && (
          <div
            data-testid="media-strip"
            className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 animate-fade-in-up"
          >
            {(post.images || []).slice(1).map((img, i) => (
              <img
                key={`img-${i}`}
                src={img}
                alt=""
                className="w-24 h-24 rounded-2xl object-cover border border-[#E4E8EF] flex-shrink-0"
              />
            ))}
            {(post.videos || []).map((v, i) => (
              <video
                key={`vid-${i}`}
                data-testid={`detail-video-${i}`}
                src={v}
                controls
                playsInline
                preload="metadata"
                className="w-40 h-24 rounded-2xl object-cover border border-[#E4E8EF] flex-shrink-0 bg-[#0B1620]"
              />
            ))}
          </div>
        )}

        {/* Title & Price */}
        <div className="animate-fade-in-up">
          <h1 className="text-2xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight">{post.title}</h1>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-2xl font-bold text-[#0B4F3F] font-['Bricolage_Grotesque']">
              ₹{post.price_per_unit}<span className="text-sm font-normal text-[#5B6675]">/{post.unit}</span>
            </span>
            <div className="flex items-center gap-3 text-xs text-[#5B6675]">
              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {post.views}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDate(post.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Quantity */}
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 flex items-center gap-3 animate-fade-in-up stagger-1">
          <div className="w-10 h-10 rounded-xl bg-[#EFF2F7] flex items-center justify-center">
            <Package className="w-5 h-5 text-[#0B4F3F]" />
          </div>
          <div>
            <p className="text-xs text-[#5B6675]">Available Quantity</p>
            <p className="text-base font-semibold text-[#0B1620]">{post.quantity} {post.unit}</p>
          </div>
        </div>

        {/* Description */}
        <div className="animate-fade-in-up stagger-2">
          <h3 className="text-sm font-semibold text-[#0B1620] mb-2 font-['Bricolage_Grotesque']">Description</h3>
          <p className="text-sm text-[#5B6675] leading-relaxed">{post.description}</p>
        </div>

        {/* Location */}
        {post.location && (
          <div className="flex items-center gap-2 text-sm text-[#5B6675] animate-fade-in-up stagger-3">
            <MapPin className="w-4 h-4 text-[#0B4F3F]" />
            {post.location}
          </div>
        )}

        {/* Farmer Card */}
        <div
          className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 animate-fade-in-up stagger-4"
          data-testid="farmer-card"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/farmer/${post.farmer_id}`)}
              className="flex-shrink-0 active:scale-95 transition-transform"
              aria-label="View farmer profile"
            >
              <Avatar name={post.farmer_name} src={post.farmer_avatar} size={48} className="border-2 border-[#E4E8EF]" />
            </button>

            <button
              type="button"
              onClick={() => navigate(`/farmer/${post.farmer_id}`)}
              className="flex-1 min-w-0 text-left active:opacity-70 transition-opacity"
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-base font-semibold text-[#0B1620]">{post.farmer_name}</span>
                <span className="px-1.5 py-0.5 bg-[#15803D]/10 text-[#15803D] text-[10px] font-bold rounded">VERIFIED</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5" data-testid="detail-farmer-rating">
                <StarRating value={farmerSummary?.avg_rating || 0} size={12} />
                <span className="text-[11px] text-[#5B6675]">
                  {farmerSummary?.review_count > 0
                    ? `${farmerSummary.avg_rating.toFixed(1)} (${farmerSummary.review_count})`
                    : 'No ratings'}
                </span>
              </div>
              {post.farmer_location && (
                <p className="text-xs text-[#5B6675] flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {post.farmer_location}
                </p>
              )}
            </button>

            {user && post.farmer_id !== user._id && farmerSummary && (
              <LikeFarmerButton
                farmerId={post.farmer_id}
                initialFollowed={farmerSummary.is_followed}
                variant="icon"
                testId="detail-like-farmer-btn"
                onChange={(f) => setFarmerSummary((s) => ({ ...s, is_followed: f }))}
              />
            )}
          </div>
        </div>

        {/* Owner-only: direct status toggle */}
        {isOwner && (
          <button
            type="button"
            data-testid="owner-toggle-status-btn"
            onClick={handleToggleStatus}
            disabled={togglingStatus}
            className={`w-full flex items-center justify-center gap-2 h-12 rounded-2xl text-sm font-semibold transition-colors animate-fade-in-up ${
              post.is_available
                ? 'bg-[#B91C1C]/5 text-[#B91C1C] hover:bg-[#B91C1C]/10 border border-[#B91C1C]/20'
                : 'bg-[#15803D]/5 text-[#15803D] hover:bg-[#15803D]/10 border border-[#15803D]/20'
            }`}
          >
            {post.is_available ? (
              <><ToggleLeft className="w-4 h-4" /> Mark this listing as Sold Out</>
            ) : (
              <><ToggleRight className="w-4 h-4" /> Mark as Available again</>
            )}
          </button>
        )}

        {/* Admin-only moderation strip (only when NOT the owner — owner already has the direct toggle) */}
        {isAdmin && !isOwner && (
          <div
            data-testid="admin-moderation-strip"
            className="bg-[#F6F8FB] rounded-2xl border border-[#0B4F3F]/25 p-3 animate-fade-in-up"
          >
            <p className="text-[10px] uppercase tracking-[0.14em] text-[#0B4F3F] font-semibold mb-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0B4F3F]" /> Admin controls
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="admin-detail-toggle-btn"
                onClick={() => setAdminToggleOpen(true)}
                disabled={togglingStatus}
                className={`flex-1 h-10 rounded-full text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors border ${
                  post.is_available
                    ? 'bg-[#F6F8FB] text-[#B91C1C] border-[#B91C1C]/25 hover:bg-[#B91C1C]/8'
                    : 'bg-[#F6F8FB] text-[#15803D] border-[#15803D]/25 hover:bg-[#15803D]/8'
                } disabled:opacity-50`}
              >
                {post.is_available
                  ? (<><PackageX className="w-3.5 h-3.5" /> Mark sold</>)
                  : (<><PackageCheck className="w-3.5 h-3.5" /> Mark available</>)}
              </button>
              <button
                type="button"
                data-testid="admin-detail-delete-btn"
                onClick={() => setAdminDeleteOpen(true)}
                disabled={adminDeleting}
                className="h-10 px-3 rounded-full text-[12px] font-semibold flex items-center justify-center gap-1.5 border border-[#DC2626]/25 text-[#DC2626] bg-[#F6F8FB] hover:bg-[#DC2626]/8 transition-colors disabled:opacity-50"
                aria-label="Delete this post"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
        )}

        {/* Request Callback CTA */}
        <div className="space-y-3 animate-fade-in-up stagger-5 pb-6">
          {callbackReq ? (
            <div
              data-testid="callback-awaiting-state"
              className="w-full rounded-2xl border border-[#15803D]/30 bg-[#15803D]/5 px-4 py-4 flex items-start gap-3"
            >
              <div className="w-9 h-9 rounded-full bg-[#15803D]/15 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-[#15803D]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#15803D] font-['Bricolage_Grotesque']">
                  Waiting for the Frootex team
                </p>
                <p className="text-xs text-[#5B6675] mt-0.5 leading-relaxed">
                  We&apos;ve received your request and will call you on <span className="font-semibold text-[#0B1620]">{callbackReq.user_phone || 'your registered number'}</span> shortly.
                </p>
                {callbackReq.status === 'contacted' && (
                  <p className="text-[11px] text-[#15803D] mt-1 font-medium">Our team has reached out — please check your phone.</p>
                )}
              </div>
            </div>
          ) : (
            <Button
              data-testid="request-callback-btn"
              onClick={handleRequestCallback}
              disabled={!post.is_available || requestingCallback}
              className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PhoneCall className="w-4 h-4 mr-2" />
              {!post.is_available
                ? 'Currently Unavailable'
                : requestingCallback
                  ? 'Sending request…'
                  : 'Request a Call Back'}
            </Button>
          )}
          {!callbackReq && post.is_available && (
            <p className="text-[11px] text-center text-[#5B6675]/80">
              Our team will reach out to verify the order and connect you with the farmer.
            </p>
          )}
        </div>
      </div>
      <BottomNav />

      {/* Admin: confirm toggle */}
      <ConfirmSheet
        open={adminToggleOpen}
        onOpenChange={setAdminToggleOpen}
        title={post.is_available ? 'Mark this post as sold?' : 'Mark this post as available?'}
        description={
          post.is_available
            ? `"${post.title}" will be hidden from vendor search results and marked sold-out. You can re-list it later.`
            : `"${post.title}" will be visible again in vendor search results and shown as available.`
        }
        confirmLabel={post.is_available ? 'Mark sold' : 'Mark available'}
        cancelLabel="Cancel"
        destructive={post.is_available}
        icon={post.is_available
          ? <PackageX className="w-6 h-6" strokeWidth={2.2} />
          : <PackageCheck className="w-6 h-6" strokeWidth={2.2} />
        }
        onConfirm={handleToggleStatus}
        loading={togglingStatus}
        testId="admin-detail-toggle"
      />

      {/* Admin: confirm delete */}
      <ConfirmSheet
        open={adminDeleteOpen}
        onOpenChange={setAdminDeleteOpen}
        title="Delete this post permanently?"
        description={`"${post.title}" will be removed from the marketplace forever. This action cannot be undone.`}
        confirmLabel="Delete post"
        cancelLabel="Keep it"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={handleAdminDelete}
        loading={adminDeleting}
        testId="admin-detail-delete"
      />
    </div>
  );
}
